import { MAX_TABLE_ROWS } from '@/main/app.constants';
import { isCanceled } from '@/utilities/http.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { TableJoinInput } from '@/tableDefinitionEditor/tableDefinition.types';
import type { Filter } from '@seeqdev/api/model/Filter';
import type { PropertiesForItemUUIDColumn, TableFormula, TableResultOutputV1 } from '@seeqdev/api/model/models';

export interface FetchMaterializedTableInput {
  tableDefinitionId: string;
  columnsToInclude?: string[];
  propertiesToInclude?: PropertiesForItemUUIDColumn[];
  includeOverrides?: boolean;
  includePublicationProvenance?: boolean;
  tableFormulas?: TableFormula[];
  cancellationGroup?: string;
  limit?: number;
  cursorKey?: string;
  filter?: Filter;
  tablesToJoin?: TableJoinInput[];
}
const fetchMaterializedTable = async ({
  tableDefinitionId,
  columnsToInclude,
  propertiesToInclude,
  includeOverrides,
  includePublicationProvenance,
  tableFormulas,
  tablesToJoin,
  cancellationGroup,
  limit,
  cursorKey,
  filter,
}: FetchMaterializedTableInput): Promise<TableResultOutputV1> => {
  const { data } = await sqTablesApi.queryTableData(
    {
      limit: limit || MAX_TABLE_ROWS,
      columnsToInclude,
      propertiesToInclude,
      includeOverrides,
      ...(includePublicationProvenance && { includePublicationProvenance: true }),
      tableFormulas,
      tablesToJoin,
      sorts: [],
      ...(cursorKey && {
        cursor: {
          cursorKey,
        },
      }),
      filter,
    },
    { tableId: tableDefinitionId },
    { cancellationGroup },
  );
  return data;
};

export const getMaterializedTable = async (
  tableDefinitionId: string,
  columnsToInclude?: string[],
  propertiesToInclude?: PropertiesForItemUUIDColumn[],
  cancellationGroup?: string,
  limit?: number,
  cursorKey?: string,
  filter?: Filter,
  tablesToJoin?: TableJoinInput[],
  includePublicationProvenance?: boolean,
): Promise<TableResultOutputV1 | undefined> => {
  try {
    return await fetchMaterializedTable({
      tableDefinitionId,
      columnsToInclude,
      propertiesToInclude,
      includeOverrides: true,
      includePublicationProvenance,
      cancellationGroup,
      limit,
      cursorKey,
      filter,
      tablesToJoin,
    });
  } catch (e) {
    if (!isCanceled(e)) {
      errorToast({ httpResponseOrError: e });
    }
    return undefined;
  }
};

export interface ConditionTableStat {
  statistic: string;
  columnName?: string;
  itemId?: string;
}

/**
 * A condition table's rows already carry the id of the scaling table backing them. Given that same
 * tableId, this resolves which of a stat column's picked signals are themselves cells of that table, so the
 * caller can send a stable `columnName` (re-resolved per row server-side) instead of a fixed `itemId`.
 */
export async function resolveScalingColumnNamesForItems(
  tableId: string,
  itemIds: string[],
): Promise<Map<string, string>> {
  const resolved = new Map<string, string>();
  if (itemIds.length === 0) {
    return resolved;
  }

  // Falls back to the caller sending a fixed itemId for these stats on any failure here -- no need to toast.
  let uuidColumnNames: string[];
  try {
    const { data: tableDefinition } = await sqTableDefinitionsApi.getTableDefinition({ id: tableId });
    uuidColumnNames = tableDefinition.columnDefinitions
      .filter((column) => column.columnType === ColumnTypeEnum.UUID)
      .map((column) => column.columnName);
  } catch {
    return resolved;
  }
  if (uuidColumnNames.length === 0) {
    return resolved;
  }

  const tableData = await getMaterializedTable(tableId, uuidColumnNames);
  if (!tableData) {
    return resolved;
  }

  const itemIdSet = new Set(itemIds);
  tableData.rows.forEach((row) => {
    row.forEach((value, index) => {
      const id = value == null ? undefined : String(value);
      if (id && itemIdSet.has(id) && !resolved.has(id)) {
        resolved.set(id, tableData.headers[index].name);
      }
    });
  });
  return resolved;
}
